import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../Firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from "firebase/storage";
import { toast } from "react-toastify";
import "../styles/EditVideo.css";

const EditVideo = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const storage = getStorage();

  const [sampleVideo, setSampleVideo] = useState(null);
  const [courseVideos, setCourseVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
   /* ================= HELPERS ================= */

   const formatDuration = totalSec => {
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = Math.floor(totalSec % 60);

    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const getVideoDuration = (file) =>
  new Promise(resolve => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(Math.floor(video.duration));
    };
    video.src = URL.createObjectURL(file);
  });



  /* ================= LOAD VIDEOS ================= */
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, "courses", courseId));
        if (!snap.exists()) {
          toast.error("Course not found");
          navigate(-1);
          return;
        }

        const videos = snap.data().videos || [];

        const sample = videos.find(v => v.isSample === true) || null;

        const course = videos
          .filter(v => v.isSample === false)
          .map(v => ({
            ...v,
            duration: v.duration || 0,
            newFile: null
          }));

        setSampleVideo(
          sample
            ? { ...sample, duration: sample.duration || 0, newFile: null }
            : null
        );

        setCourseVideos(course);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load videos");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [courseId, navigate]);

  /* ================= ADD COURSE VIDEO ================= */
  const addCourseVideo = () => {
    setCourseVideos(prev => [
      ...prev,
      {
        id: Date.now().toString(),
        title: "",
        description: "",
        url: "",
        isSample: false,
        newFile: null
      }
    ]);
  };

  /* ================= UPDATE HANDLERS ================= */
  const updateSample = (field, value) => {
    setSampleVideo(prev => ({ ...prev, [field]: value }));
  };

  const updateCourseVideo = (index, field, value) => {
    const updated = [...courseVideos];
    updated[index][field] = value;
    setCourseVideos(updated);
  };

  const handleFileChange = (setter, index, file) => {
    if (index === null) {
      setter(prev => ({ ...prev, newFile: file }));
    } else {
      const updated = [...courseVideos];
      updated[index].newFile = file;
      setCourseVideos(updated);
    }
  };

  /* ================= DELETE COURSE VIDEO ================= */
  const removeCourseVideo = (index) => {
    if (!window.confirm("Delete this course video?")) return;
    setCourseVideos(courseVideos.filter((_, i) => i !== index));
  };

  const saveVideos = async () => {
    try {
      setSaving(true);

      let totalDuration = 0;
      const finalVideos = [];

      /* ========= SAMPLE VIDEO ========= */
      if (sampleVideo) {
        let url = sampleVideo.url;
        let duration = sampleVideo.duration || 0;

        if (sampleVideo.newFile) {
          const refPath = ref(
            storage,
            `course-videos/${courseId}/sample-${Date.now()}`
          );

          await uploadBytes(refPath, sampleVideo.newFile);
          url = await getDownloadURL(refPath);

          duration = await getVideoDuration(sampleVideo.newFile);
        }

        finalVideos.push({
          id: "sample",
          title: sampleVideo.title,
          description: sampleVideo.description || "",
          url,
          isSample: true,
          duration
        });
      }

      /* ========= COURSE VIDEOS ========= */
      for (const video of courseVideos) {
        let url = video.url;
        let duration = video.duration || 0;

        if (video.newFile) {
          const refPath = ref(
            storage,
            `course-videos/${courseId}/${video.id}-${Date.now()}`
          );

          await uploadBytes(refPath, video.newFile);
          url = await getDownloadURL(refPath);

          duration = await getVideoDuration(video.newFile);
        }

        // count only non sample videos
        totalDuration += duration;

        finalVideos.push({
          id: video.id,
          title: video.title,
          description: video.description || "",
          url,
          isSample: false,
          duration
        });
      }

      /* ========= UPDATE COURSE ========= */
      await updateDoc(doc(db, "courses", courseId), {
        videos: finalVideos,
        previewVideo: finalVideos[0]?.url || "",
        duration: formatDuration(totalDuration),
        durationSeconds: totalDuration
      });

      toast.success("Videos updated successfully");
      navigate(-1);

    } catch (err) {
      console.error(err);
      toast.error("Failed to save videos");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p style={{ padding: 40 }}>Loading videos...</p>;
  }

  return (
    <div className="edit-video-page">
      <h1>Edit Videos</h1>

      {/* ================= SAMPLE VIDEO ================= */}
      <h2 className="video-section-title">Sample Video</h2>

      {sampleVideo ? (
        <div className="video-card">
          <input
            placeholder="Sample video title"
            value={sampleVideo.title}
            onChange={(e) =>
              updateSample("title", e.target.value)
            }
          />

          <textarea
            placeholder="Sample video description"
            value={sampleVideo.description || ""}
            onChange={(e) =>
              updateSample("description", e.target.value)
            }
          />

          {sampleVideo.url && (
            <video
              src={sampleVideo.url}
              controls
              className="video-preview"
            />
          )}

          <input
            type="file"
            accept="video/*"
            onChange={(e) =>
              handleFileChange(setSampleVideo, null, e.target.files[0])
            }
          />
        </div>
      ) : (
        <button
          className="add-video"
          onClick={() =>
            setSampleVideo({
              id: "sample",
              title: "",
              description: "",
              url: "",
              isSample: true,
              newFile: null
            })
          }
        >
          + Add Sample Video
        </button>
      )}

      {/* ================= COURSE VIDEOS ================= */}
      <h2 className="video-section-title">Course Videos</h2>

      {courseVideos.map((video, index) => (
        <div key={video.id} className="video-card">
          <input
            placeholder="Video title"
            value={video.title}
            onChange={(e) =>
              updateCourseVideo(index, "title", e.target.value)
            }
          />

          <textarea
            placeholder="Video description"
            value={video.description || ""}
            onChange={(e) =>
              updateCourseVideo(
                index,
                "description",
                e.target.value
              )
            }
          />

          {video.url && (
            <video
              src={video.url}
              controls
              className="video-preview"
            />
          )}

          <input
            type="file"
            accept="video/*"
            onChange={(e) =>
              handleFileChange(null, index, e.target.files[0])
            }
          />

          <button
            className="delete-video"
            onClick={() => removeCourseVideo(index)}
          >
            Delete
          </button>
        </div>
      ))}

      <button className="add-video" onClick={addCourseVideo}>
        + Add Course Video
      </button>

      {/* ================= ACTIONS ================= */}
      <div className="action-buttons">
        <button onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </button>
        <button onClick={saveVideos} disabled={saving}>
          {saving ? "Saving..." : "Save Videos"}
        </button>
      </div>
    </div>
  );
};

export default EditVideo;
